import type {
  Wallet,
  WalletOption,
  DynamicWalletKey,
} from '@stripe-internal/wallet-connect-wrapper';
import type {NetworkType, SupportedCurrency} from 'src/v2/state/types';
import {EVM_NETWORKS} from 'src/types';
import {isEmbedded, isMobile} from 'src/utils/utils';
import {getNetworkTypeFromWalletKey} from 'src/v2/util/getWalletKey';
import {isNetworkTypeDisabledForProvider} from 'src/v2/util/isNetworkTypeDisabledForProvider';
import {getDisabledNetworksForProvider} from 'src/v2/util/isWalletProviderDisabled';

// Returns true to keep the option, false to remove it
type WalletFilter = (option: WalletOption) => boolean;

interface FilterContext {
  supportedCurrencies: SupportedCurrency[];
  connectedTypes: Set<string>;
  disabledWalletProviders: string[];
}

const WALLETS_UNSUPPORTED_IN_MOBILE_EMBED = ['Phantom', 'Coinbase Wallet'];

const getNetworkTypeForCurrency = (
  currency: SupportedCurrency,
): NetworkType | null => {
  if (EVM_NETWORKS.includes(currency.currencyNetwork)) {
    return 'evm';
  }

  if (currency.currencyNetwork === 'solana') {
    return 'solana';
  }

  return null;
};

const getAvailableNetworkTypesForWalletOption = (
  walletOption: WalletOption,
  supportedCurrencies: SupportedCurrency[],
  disabledWalletProviders: string[],
): NetworkType[] => {
  const disabledNetworks = getDisabledNetworksForProvider(
    walletOption.provider,
    disabledWalletProviders,
  );
  const availableNetworkTypes = new Set<NetworkType>();

  supportedCurrencies.forEach((currency) => {
    // Skip concrete networks disabled for this provider.
    if (disabledNetworks.includes(currency.currencyNetwork)) {
      return;
    }

    // Wallet connection is selected by network type. Multiple concrete EVM
    // networks collapse to one "evm" connection option.
    const networkType = getNetworkTypeForCurrency(currency);
    if (!networkType) {
      return;
    }

    if (
      isNetworkTypeDisabledForProvider(
        walletOption.provider,
        networkType,
        disabledWalletProviders,
      )
    ) {
      return;
    }

    availableNetworkTypes.add(networkType);
  });

  return Array.from(availableNetworkTypes);
};

const getConnectedWalletTypes = (connectedWallets: Wallet[]): Set<string> => {
  const connectedTypes = new Set<string>();

  connectedWallets.forEach((wallet) => {
    const provider = wallet.type;
    const networkType = getNetworkTypeFromWalletKey(
      wallet.key as DynamicWalletKey,
    );

    // Unique key for provider + network combination
    connectedTypes.add(`${provider}:${networkType}`);
  });

  return connectedTypes;
};

// --- Filter predicates ---

const filterUnsupportedInMobileEmbed = (): WalletFilter => {
  const isMobileEmbedded = isMobile() && isEmbedded();

  return (option) =>
    !(
      isMobileEmbedded &&
      WALLETS_UNSUPPORTED_IN_MOBILE_EMBED.includes(option.provider)
    );
};

const filterConnectableWallets = (context: FilterContext): WalletFilter => {
  return (option) => {
    const availableNetworkTypes = getAvailableNetworkTypesForWalletOption(
      option,
      context.supportedCurrencies,
      context.disabledWalletProviders,
    );

    // Keep the wallet if it has at least one available network type that is
    // not already connected for this provider.
    return availableNetworkTypes.some(
      (networkType) =>
        !context.connectedTypes.has(`${option.provider}:${networkType}`),
    );
  };
};

const buildFilters = (context: FilterContext): WalletFilter[] => [
  filterUnsupportedInMobileEmbed(),
  filterConnectableWallets(context),
];

/**
 * Filters wallet options based on device/embed constraints, network relevance,
 * and already-connected state.
 */
export const filterAvailableWalletOptions = (
  allWalletOptions: WalletOption[],
  connectedWallets: Wallet[],
  supportedCurrencies: SupportedCurrency[],
  disabledWalletProviders: string[] = [],
): WalletOption[] => {
  const context: FilterContext = {
    supportedCurrencies,
    connectedTypes: getConnectedWalletTypes(connectedWallets),
    disabledWalletProviders,
  };

  const filters = buildFilters(context);
  return allWalletOptions.filter((option) => filters.every((f) => f(option)));
};
